import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { companyInfo } from '../utils/dataInfo';

// Helper functions
const formatCurrency = (amount) => {
return new Intl.NumberFormat('en-US', {
    // style: 'currency',
    // currency: 'USD'
}).format(amount || 0);
};

const formatNumber = (num) => {
return new Intl.NumberFormat('en-US').format(num || 0);
};

const formatDate = (date) => {
return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
});
};

const getStatusColor = (status) => {
switch (status?.toLowerCase()) {
    case 'approved': return '#4caf50';
    case 'submitted': return '#2196f3';
    case 'draft': return '#ff9800';
    case 'rejected': return '#f44336';
    default: return '#757575';
}
};

const getVarianceColor = (variance) => {
if (variance > 0) return '#4caf50';
if (variance < 0) return '#f44336';
return '#2196f3';
};

// Generate Excel Report
export const exportToExcel = async (reportData, filters = {}) => {
try {
    const workbook = XLSX.utils.book_new();
    
    // Main Sheet
    const mainHeaders = [
    'Closing Number',
    'Closing Date',
    'Branch',
    'User',
    'Opening Balance',
    'Sales Total',
    'Closing Balance',
    'Expected Cash',
    'Actual Cash',
    'Variance',
    'Status',
    'Notes',
    'Created Date'
    ];

    const mainData = reportData.closings?.map(closing => [
    closing.clossing_number,
    formatDate(closing.clossing_date),
    closing.branch?.name || 'N/A',
    closing.user?.name || 'N/A',
    closing.opening_balance,
    closing.sales_total,
    closing.clossing_balance,
    closing.expected_cash,
    closing.actual_cash,
    closing.variance,
    closing.status.charAt(0).toUpperCase() + closing.status.slice(1),
    closing.notes || '',
    formatDate(closing.created_at)
    ]) || [];

    const mainSheetData = [mainHeaders, ...mainData];
    const mainSheet = XLSX.utils.aoa_to_sheet(mainSheetData);
    XLSX.utils.book_append_sheet(workbook, mainSheet, 'Day Closings');

    // Summary Sheet
    const summarySheetData = [
    ['DAY CLOSING REPORT SUMMARY'],
    ['Generated on', new Date().toLocaleString()],
    [''],
    ['Total Closings', reportData.summary.total_closings],
    ['Total Sales', formatCurrency(reportData.summary.total_sales)],
    ['Total Expected Cash', formatCurrency(reportData.summary.total_expected_cash)],
    ['Total Actual Cash', formatCurrency(reportData.summary.total_actual_cash)],
    ['Total Variance', formatCurrency(reportData.summary.total_variance)],
    [''],
    ['FILTERS APPLIED'],
    ['Branch', filters.branch_name || 'All'],
    ['Status', filters.status || 'All'],
    ['Date From', filters.dateFrom || 'All'],
    ['Date To', filters.dateTo || 'All']
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summarySheetData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Items Sheet (if available)
    const allItems = [];
    reportData.closings?.forEach(closing => {
    closing.items?.forEach(item => {
        allItems.push({
        'Closing Number': closing.clossing_number,
        'Item Name': item.stock?.name || 'N/A',
        'Opening Quantity': item.opening_quantity,
        'Sold Quantity': item.sold_quantity,
        'Closing Quantity': item.clossing_quantity,
        'Opening Value': item.opening_value,
        'Sales Value': item.sales_value,
        'Closing Value': item.clossing_value
        });
    });
    });

    if (allItems.length > 0) {
    const itemsSheet = XLSX.utils.json_to_sheet(allItems);
    XLSX.utils.book_append_sheet(workbook, itemsSheet, 'Items');
    }

    // Set column widths
    const sheets = workbook.SheetNames;
    sheets.forEach(sheetName => {
    const worksheet = workbook.Sheets[sheetName];
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const colWidths = [];
    
    for (let col = range.s.c; col <= range.e.c; col++) {
        let maxWidth = 0;
        for (let row = range.s.r; row <= range.e.r; row++) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
        if (cell && cell.v) {
            const cellWidth = String(cell.v).length;
            maxWidth = Math.max(maxWidth, cellWidth);
        }
        }
        colWidths.push({ wch: Math.min(maxWidth + 2, 50) });
    }
    
    worksheet['!cols'] = colWidths;
    });

    // Generate file name
    const fileName = `day_closing_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// Generate PDF Report
export const exportToPDF = async (reportData, filters = {}) => {
try {
    const doc = new jsPDF('landscape');
    
    // Set document properties
    doc.setProperties({
    title: 'Day Closing Report',
    subject: 'Daily Closing and Cash Management Report',
    author: companyInfo.name,
    keywords: 'day closing, cash, report, sales',
    creator: companyInfo.name
    });

    // Title Page
    doc.setFontSize(24);
    doc.setTextColor(33, 150, 243);
    doc.text('DAY CLOSING REPORT', 105, 50, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setTextColor(66, 66, 66);
    doc.text(companyInfo.name, 105, 65, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(97, 97, 97);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 105, 75, { align: 'center' });
    
    if (filters.dateFrom && filters.dateTo) {
    doc.text(`Period: ${formatDate(filters.dateFrom)} to ${formatDate(filters.dateTo)}`, 105, 82, { align: 'center' });
    }

    // Summary Section
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('EXECUTIVE SUMMARY', 20, 20);

    doc.setFontSize(11);
    doc.setTextColor(66, 66, 66);
    
    let yPos = 35;
    const summaryItems = [
    ['Total Closings:', formatNumber(reportData.summary.total_closings)],
    ['Total Sales:', formatCurrency(reportData.summary.total_sales)],
    ['Total Expected Cash:', formatCurrency(reportData.summary.total_expected_cash)],
    ['Total Actual Cash:', formatCurrency(reportData.summary.total_actual_cash)],
    ['Total Variance:', formatCurrency(reportData.summary.total_variance)],
    ['Average Sales per Closing:', formatCurrency(reportData.summary.total_sales / (reportData.summary.total_closings || 1))],
    ['Average Variance per Closing:', formatCurrency(reportData.summary.total_variance / (reportData.summary.total_closings || 1))]
    ];

    summaryItems.forEach(([label, value], index) => {
    const x = 20;
    const y = yPos + index * 10;
    
    doc.setTextColor(97, 97, 97);
    doc.text(label, x, y);
    doc.setTextColor(33, 33, 33);
    doc.text(value, x + 70, y);
    });

    // Main Table
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('DAY CLOSING DETAILS', 20, 20);

    const tableData = reportData.closings?.map(closing => [
    closing.clossing_number,
    formatDate(closing.clossing_date),
    formatCurrency(closing.sales_total),
    formatCurrency(closing.expected_cash),
    formatCurrency(closing.actual_cash),
    formatCurrency(closing.variance),
    closing.status.charAt(0).toUpperCase() + closing.status.slice(1)
    ]) || [];

    autoTable(doc, {
    startY: 30,
    head: [['Closing #', 'Date','Sales', 'Expected', 'Actual', 'Variance', 'Status']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [33, 150, 243], textColor: 255 },
    columnStyles: {
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' }
    },
    didDrawCell: (data) => {
        // Color code variance
        if (data.column.index === 6 && data.cell.raw) {
        const variance = parseFloat(data.cell.raw.replace(/[^0-9.-]+/g, ""));
        const color = getVarianceColor(variance);
        doc.setTextColor(color);
        }
        
        // Color code status
        if (data.column.index === 7) {
        const color = getStatusColor(data.cell.raw);
        doc.setTextColor(color);
        }
    }
    });

    // Status Distribution
    if (reportData.closings?.length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('STATUS DISTRIBUTION', 20, 20);

    const statusCounts = {};
    reportData.closings.forEach(closing => {
        const status = closing.status;
        statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    const statusData = Object.entries(statusCounts).map(([status, count]) => [
        status.charAt(0).toUpperCase() + status.slice(1),
        count,
        `${((count / reportData.closings.length) * 100).toFixed(1)}%`
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Status', 'Count', 'Percentage']],
        body: statusData,
        theme: 'grid',
        headStyles: { fillColor: [76, 175, 80], textColor: 255 },
        columnStyles: {
        2: { halign: 'right' }
        },
        didDrawCell: (data) => {
        if (data.column.index === 0 && data.cell.raw) {
            const color = getStatusColor(data.cell.raw.toLowerCase());
            doc.setTextColor(color);
        }
        }
    });
    }

    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
    doc.text(companyInfo.name, 20, doc.internal.pageSize.height - 10);
    }

    const fileName = `day_closing_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
}
};

// Generate CSV Report
export const exportToCSV = async (reportData, filters = {}) => {
try {
    const csvLines = [];
    
    // Header
    csvLines.push('DAY CLOSING REPORT');
    csvLines.push(`Generated on: ${new Date().toLocaleString()}`);
    csvLines.push(`Company: ${companyInfo.name}`);
    csvLines.push('');
    
    // Summary
    csvLines.push('SUMMARY');
    csvLines.push('Metric,Value');
    csvLines.push(`Total Closings,${reportData.summary.total_closings}`);
    csvLines.push(`Total Sales,${formatCurrency(reportData.summary.total_sales)}`);
    csvLines.push(`Total Expected Cash,${formatCurrency(reportData.summary.total_expected_cash)}`);
    csvLines.push(`Total Actual Cash,${formatCurrency(reportData.summary.total_actual_cash)}`);
    csvLines.push(`Total Variance,${formatCurrency(reportData.summary.total_variance)}`);
    csvLines.push('');
    
    // Main Data
    csvLines.push('DAY CLOSING DETAILS');
    csvLines.push('Closing Number,Closing Date,Branch,User,Opening Balance,Sales Total,Closing Balance,Expected Cash,Actual Cash,Variance,Status,Notes');
    
    reportData.closings?.forEach(closing => {
    csvLines.push([
        `"${closing.clossing_number.replace(/"/g, '""')}"`,
        formatDate(closing.clossing_date),
        `"${(closing.branch?.name || 'N/A').replace(/"/g, '""')}"`,
        `"${(closing.user?.name || 'N/A').replace(/"/g, '""')}"`,
        closing.opening_balance,
        closing.sales_total,
        closing.clossing_balance,
        closing.expected_cash,
        closing.actual_cash,
        closing.variance,
        closing.status.charAt(0).toUpperCase() + closing.status.slice(1),
        `"${(closing.notes || '').replace(/"/g, '""')}"`
    ].join(','));
    });
    
    // Items Data
    if (reportData.closings?.some(c => c.items?.length > 0)) {
    csvLines.push('');
    csvLines.push('CLOSING ITEMS');
    csvLines.push('Closing Number,Item Name,Opening Quantity,Sold Quantity,Closing Quantity,Opening Value,Sales Value,Closing Value');
    
    reportData.closings.forEach(closing => {
        closing.items?.forEach(item => {
        csvLines.push([
            `"${closing.clossing_number.replace(/"/g, '""')}"`,
            `"${(item.stock?.name || 'N/A').replace(/"/g, '""')}"`,
            item.opening_quantity,
            item.sold_quantity,
            item.clossing_quantity,
            item.opening_value,
            item.sales_value,
            item.clossing_value
        ].join(','));
        });
    });
    }

    // Create CSV file
    const csvContent = csvLines.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `day_closing_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};

// Quick PDF Export
export const exportDayClossingReportQuickPDF = async (reportData) => {
try {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text('Day Closing Quick Report', 20, 20);
    
    // Summary
    doc.setFontSize(12);
    let y = 40;
    
    const quickSummary = [
    `Total Closings: ${formatNumber(reportData.summary.total_closings)}`,
    `Total Sales: ${formatCurrency(reportData.summary.total_sales)}`,
    `Total Expected Cash: ${formatCurrency(reportData.summary.total_expected_cash)}`,
    `Total Actual Cash: ${formatCurrency(reportData.summary.total_actual_cash)}`,
    `Total Variance: ${formatCurrency(reportData.summary.total_variance)}`
    ];
    
    quickSummary.forEach(line => {
    doc.text(line, 20, y);
    y += 10;
    });
    
    y += 10;
    
    // Recent Closings
    if (reportData.closings?.length > 0) {
    doc.text('Recent Closings:', 20, y);
    y += 10;
    
    reportData.closings.slice(0, 5).forEach(closing => {
        doc.text(`• ${closing.clossing_number}: ${formatCurrency(closing.sales_total)} (${closing.status})`, 25, y);
        y += 7;
    });
    }
    
    const fileName = `day_closing_quick_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    return { success: true, fileName };
} catch (error) {
    console.error('Quick PDF export failed:', error);
    throw new Error('Failed to export quick PDF: ' + error.message);
}
};